(function (window, document) {
  "use strict";

  if (window.gleameryAnalyticsIdentityBridge) return;

  var BRIDGE_VERSION = "3";
  var RK_SESSION_ID_KEY = "rk_session_id";
  var SEGMENT_COOKIE_KEY = "gleamery_segment_anonymous_id";
  var STATE_KEY = "Analytics.identityBridge";
  var LINKED_KEY = "Analytics.identityBridgeLinked";
  var COOKIE_DOMAIN = ".thegleamery.com";
  var POLL_INTERVAL_MS = 30000;
  var RETRY_INTERVAL_MS = 500;
  var GA_LOOKUP_TIMEOUT_MS = 4000;
  var config = window.gleameryAnalyticsIdentityBridgeConfig || {};
  var state = {
    rk_session_id: "",
    ga_session_id: "",
    segment_anonymous_id: "",
  };
  var active = false;
  var pollTimer = null;
  var retryTimer = null;
  var gaLookupPending = false;
  var gaLookupStartedAt = 0;
  var segmentReadyQueued = false;
  var segmentReadyHandled = false;
  var lastGaSignature = "";
  var lastClaritySignature = "";
  var lastLinkKey = "";
  // GA4 starts a session on any hit, so a link is only sent for the GA4 session of
  // the page load or route change that requested it, never from the idle poll.
  var linkRequested = false;
  var linkGaSessionId = "";

  function hasAnalyticsConsent() {
    return Boolean(
      window.gleameryConsent &&
        typeof window.gleameryConsent.hasCategory === "function" &&
        window.gleameryConsent.hasCategory("analytics"),
    );
  }

  function getSessionValue(key) {
    try {
      return window.sessionStorage ? window.sessionStorage.getItem(key) || "" : "";
    } catch {
      return "";
    }
  }

  function setSessionValue(key, value) {
    try {
      if (window.sessionStorage) window.sessionStorage.setItem(key, value);
    } catch {
      // Browser privacy settings can make sessionStorage unavailable.
    }
  }

  function removeSessionValue(key) {
    try {
      if (window.sessionStorage) window.sessionStorage.removeItem(key);
    } catch {
      // Browser privacy settings can make sessionStorage unavailable.
    }
  }

  function readCookie(name) {
    var prefix = name + "=";
    var cookies = document.cookie ? document.cookie.split(";") : [];
    for (var index = 0; index < cookies.length; index += 1) {
      var cookie = cookies[index].trim();
      if (cookie.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(cookie.substring(prefix.length));
        } catch {
          return cookie.substring(prefix.length);
        }
      }
    }
    return "";
  }

  function writeCookie(name, value, maxAge) {
    document.cookie =
      name +
      "=" +
      encodeURIComponent(value) +
      "; Max-Age=" +
      maxAge +
      "; path=/; SameSite=Lax; domain=" +
      COOKIE_DOMAIN +
      (window.location.protocol === "https:" ? "; Secure" : "");
  }

  function expireCookie(name) {
    var secureAttribute = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = name + "=; Max-Age=0; path=/; SameSite=Lax" + secureAttribute;
    document.cookie = name + "=; Max-Age=0; path=/; SameSite=Lax; domain=" + COOKIE_DOMAIN + secureAttribute;
  }

  function createSessionId(size) {
    var alphabet = "useandom-26T198340PX75pxJACKVERYMINDBUSHWOLF_GQZbfghjklqvwyzrict";
    var length = size || 21;
    var id = "";
    if (window.crypto && typeof window.crypto.getRandomValues === "function") {
      var bytes = window.crypto.getRandomValues(new Uint8Array(length));
      while (length > 0) {
        length -= 1;
        id += alphabet[bytes[length] & 63];
      }
      return id;
    }
    return String(Date.now()) + Math.random().toString(36).slice(2);
  }

  function parseRKCookie() {
    var raw = readCookie(RK_SESSION_ID_KEY);
    if (!raw) return "";
    try {
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed.value === "string" ? parsed.value : "";
    } catch {
      return "";
    }
  }

  function getOrCreateRKSessionId() {
    if (!hasAnalyticsConsent()) return "";
    var sessionId = getSessionValue(RK_SESSION_ID_KEY) || parseRKCookie();
    if (!sessionId) sessionId = createSessionId(21);
    setSessionValue(RK_SESSION_ID_KEY, sessionId);
    document.cookie =
      RK_SESSION_ID_KEY +
      "=" +
      JSON.stringify({ value: sessionId, origin: config.origin || window.location.hostname }) +
      "; path=/; SameSite=Lax; domain=" +
      COOKIE_DOMAIN +
      (window.location.protocol === "https:" ? "; Secure" : "");
    state.rk_session_id = sessionId;
    return sessionId;
  }

  function getIdentifiers() {
    var identifiers = {
      identity_bridge_version: BRIDGE_VERSION,
    };
    if (state.rk_session_id) identifiers.rk_session_id = state.rk_session_id;
    if (state.ga_session_id) identifiers.ga_session_id = state.ga_session_id;
    if (state.segment_anonymous_id) {
      identifiers.segment_anonymous_id = state.segment_anonymous_id;
    }
    return identifiers;
  }

  function saveState() {
    setSessionValue(STATE_KEY, JSON.stringify(getIdentifiers()));
  }

  function setSegmentAnonymousId(anonymousId) {
    if (!hasAnalyticsConsent()) return;
    if (!anonymousId || typeof anonymousId !== "string") return;
    if (state.segment_anonymous_id === anonymousId) return;
    state.segment_anonymous_id = anonymousId;
    writeCookie(SEGMENT_COOKIE_KEY, anonymousId, 31536000);
    saveState();
  }

  function addIdentifiers(container) {
    var identifiers = getIdentifiers();
    var key;
    if (!container || typeof container !== "object" || Array.isArray(container)) container = {};
    for (key in identifiers) {
      if (Object.prototype.hasOwnProperty.call(identifiers, key)) {
        container[key] = identifiers[key];
      }
    }
    if (!container.metadata || typeof container.metadata !== "object" || Array.isArray(container.metadata)) {
      container.metadata = {};
    }
    for (key in identifiers) {
      if (Object.prototype.hasOwnProperty.call(identifiers, key)) {
        container.metadata[key] = identifiers[key];
      }
    }
    return container;
  }

  function addContext(message) {
    if (!message) return;
    if (!message.context || typeof message.context !== "object" || Array.isArray(message.context)) {
      message.context = {};
    }
    message.context.gleamery_identity = getIdentifiers();
  }

  function enrichSegmentPayload(payload) {
    if (!payload) return payload;
    if (!hasAnalyticsConsent()) return payload;
    var type = typeof payload.type === "function" ? payload.type() : "";
    var messages = [];
    if (payload.obj) messages.push(payload.obj);
    if (payload.raw && payload.raw !== payload.obj) messages.push(payload.raw);

    for (var index = 0; index < messages.length; index += 1) {
      var message = messages[index];
      var anonymousId = message.anonymousId || message.anonymous_id;
      if (anonymousId) setSegmentAnonymousId(String(anonymousId));
      addContext(message);
      if (type === "identify" || type === "group") {
        message.traits = addIdentifiers(message.traits);
      } else if (type === "track" || type === "page" || type === "screen") {
        message.properties = addIdentifiers(message.properties);
      }
    }
    return payload;
  }

  function prepareSegment(analytics) {
    if (!analytics || !hasAnalyticsConsent()) return;
    getOrCreateRKSessionId();
    var sharedAnonymousId = readCookie(SEGMENT_COOKIE_KEY);
    if (sharedAnonymousId) {
      setSegmentAnonymousId(sharedAnonymousId);
      if (typeof analytics.setAnonymousId === "function") {
        analytics.setAnonymousId(sharedAnonymousId);
      }
    }
    if (!analytics._gleameryIdentityMiddlewareAdded && typeof analytics.addSourceMiddleware === "function") {
      analytics._gleameryIdentityMiddlewareAdded = true;
      analytics.addSourceMiddleware(function (middleware) {
        enrichSegmentPayload(middleware.payload);
        middleware.next(middleware.payload);
      });
    }
  }

  function readSegmentAnonymousId() {
    var analytics = window.analytics;
    if (!analytics) return;
    prepareSegment(analytics);
    try {
      if (typeof analytics.user === "function") {
        var user = analytics.user();
        if (user && typeof user.anonymousId === "function") {
          setSegmentAnonymousId(String(user.anonymousId() || ""));
        }
      }
    } catch {
      // Analytics.js may still be replacing its preload queue.
    }

    if (!segmentReadyQueued && !segmentReadyHandled && typeof analytics.ready === "function") {
      segmentReadyQueued = true;
      analytics.ready(function () {
        segmentReadyQueued = false;
        segmentReadyHandled = true;
        readSegmentAnonymousId();
        syncProviders();
      });
    }
  }

  function readGaSessionId() {
    var measurementId = config.ga4MeasurementId;
    if (!measurementId || typeof window.gtag !== "function") return;
    var now = Date.now();
    if (gaLookupPending && now - gaLookupStartedAt < GA_LOOKUP_TIMEOUT_MS) return;
    gaLookupPending = true;
    gaLookupStartedAt = now;
    window.gtag("get", measurementId, "session_id", function (sessionId) {
      gaLookupPending = false;
      if (!active || !hasAnalyticsConsent() || !sessionId) return;
      var normalized = String(sessionId);
      if (linkRequested && !linkGaSessionId) linkGaSessionId = normalized;
      if (state.ga_session_id !== normalized) {
        state.ga_session_id = normalized;
        saveState();
      }
      syncProviders();
    });
  }

  function syncGa4() {
    if (typeof window.gtag !== "function" || !state.rk_session_id || !state.segment_anonymous_id) return;
    var identifiers = getIdentifiers();
    var signature = JSON.stringify(identifiers);
    if (signature === lastGaSignature) return;
    lastGaSignature = signature;
    window.gtag("set", identifiers);
  }

  function syncClarity() {
    if (typeof window.clarity !== "function" || !state.rk_session_id || !state.segment_anonymous_id) return;
    var signature = [
      state.rk_session_id,
      state.ga_session_id,
      state.segment_anonymous_id,
      window.location.pathname,
    ].join(":");
    if (signature === lastClaritySignature) return;
    lastClaritySignature = signature;
    window.clarity("set", "rk_session_id", state.rk_session_id);
    window.clarity("set", "segment_anonymous_id", state.segment_anonymous_id);
    if (state.ga_session_id) window.clarity("set", "ga_session_id", state.ga_session_id);
    window.clarity(
      "identify",
      state.segment_anonymous_id,
      state.rk_session_id,
      window.location.pathname.slice(0, 255),
    );
  }

  function emitLinkEvent() {
    if (!linkRequested || !linkGaSessionId) return;
    if (!state.rk_session_id || !state.ga_session_id || !state.segment_anonymous_id) return;
    if (!config.ga4MeasurementId || typeof window.gtag !== "function") return;
    if (state.ga_session_id !== linkGaSessionId) {
      // The requesting session expired before the IDs resolved. Linking the new one
      // now would open a GA4 session without a page view.
      linkRequested = false;
      return;
    }
    var linkKey = [state.rk_session_id, state.ga_session_id, state.segment_anonymous_id].join(":");
    // Distinguish the old Segment marker so an already-open session gets its GA4 join.
    var marker = "ga4:" + linkKey;
    if (lastLinkKey === marker || getSessionValue(LINKED_KEY) === marker) {
      linkRequested = false;
      return;
    }
    var properties = getIdentifiers();
    properties.send_to = config.ga4MeasurementId;
    properties.source_surface = config.surface || window.location.hostname;
    properties.page_hostname = window.location.hostname;
    properties.identity_bridge_event_id = linkKey;
    // Keep aligned with the booking flow's GA4 page-location allowlist. Never let
    // gtag fall back to a URL containing contact details or booking tokens.
    var pageLocation = new URL(window.location.pathname, window.location.origin);
    var query = new URLSearchParams(window.location.search);
    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
      "gclid",
      "gbraid",
      "wbraid",
      "fbclid",
      "location",
      "clientType",
    ].forEach(function (key) {
      query.getAll(key).forEach(function (value) {
        if (value) pageLocation.searchParams.append(key, value);
      });
    });
    properties.page_location = pageLocation.toString();
    properties.page_referrer = "";
    try {
      var referrer = new URL(document.referrer);
      properties.page_referrer = referrer.origin + referrer.pathname;
    } catch {
      // Explicitly suppress gtag's automatic referrer when none is valid.
    }
    window.gtag("event", "analytics_identity_linked", properties);
    linkRequested = false;
    lastLinkKey = marker;
    setSessionValue(LINKED_KEY, marker);
  }

  function syncProviders() {
    if (!active || !hasAnalyticsConsent()) return;
    saveState();
    syncGa4();
    syncClarity();
    emitLinkEvent();
  }

  function poll() {
    if (!active || !hasAnalyticsConsent()) return;
    getOrCreateRKSessionId();
    readSegmentAnonymousId();
    readGaSessionId();
    syncProviders();
  }

  // Called for each page load or SPA page view; requests one link for its GA4 session.
  function refresh() {
    if (!active || !hasAnalyticsConsent()) return;
    linkRequested = true;
    linkGaSessionId = "";
    poll();
  }

  function scheduleRetry() {
    if (retryTimer || (state.ga_session_id && state.segment_anonymous_id)) return;
    retryTimer = window.setTimeout(function () {
      retryTimer = null;
      poll();
      scheduleRetry();
    }, RETRY_INTERVAL_MS);
  }

  function start() {
    if (!hasAnalyticsConsent()) return;
    if (!active) {
      active = true;
      pollTimer = window.setInterval(poll, POLL_INTERVAL_MS);
    }
    refresh();
    scheduleRetry();
  }

  function reset() {
    active = false;
    if (pollTimer) window.clearInterval(pollTimer);
    if (retryTimer) window.clearTimeout(retryTimer);
    pollTimer = null;
    retryTimer = null;
    gaLookupPending = false;
    segmentReadyQueued = false;
    segmentReadyHandled = false;
    lastGaSignature = "";
    lastClaritySignature = "";
    lastLinkKey = "";
    linkRequested = false;
    linkGaSessionId = "";
    state.rk_session_id = "";
    state.ga_session_id = "";
    state.segment_anonymous_id = "";
    removeSessionValue(STATE_KEY);
    removeSessionValue(LINKED_KEY);
    removeSessionValue(RK_SESSION_ID_KEY);
    expireCookie(SEGMENT_COOKIE_KEY);
    expireCookie(RK_SESSION_ID_KEY);
  }

  window.getRKSessionId = getOrCreateRKSessionId;
  window.gleameryAnalyticsIdentityBridge = {
    version: BRIDGE_VERSION,
    enrichSegmentPayload: enrichSegmentPayload,
    getIdentifiers: getIdentifiers,
    prepareSegment: prepareSegment,
    refresh: refresh,
    reset: reset,
  };

  if (window.gleameryConsent) {
    if (typeof window.gleameryConsent.onCategory === "function") {
      window.gleameryConsent.onCategory("analytics", start);
    }
    if (typeof window.gleameryConsent.onConsentUpdate === "function") {
      window.gleameryConsent.onConsentUpdate(function () {
        if (hasAnalyticsConsent()) start();
        else reset();
      });
    }
  }
})(window, document);
